/**
 * F 156 phase 1 — the containment edge.
 *
 * FC-DESTINATION-v1.2 §12 names TWO relationships and only two: containment
 * (`parentId` + `order`) and references (`fcid://`). References shipped in F 86.
 * Containment was missing, and it was the load-bearing one — a page inside a
 * page, a lesson inside a course, a setting inside a category.
 *
 * WHY THIS IS A SEPARATE MODULE AND NOT A METHOD ON EACH KIND
 * ------------------------------------------------------------
 * `Task` already had `parentId` and `order` (per-kind, added ad hoc). Putting
 * the same three fields on every kind would give subtly different
 * implementations of "walk up the parent chain". The relationship is the same
 * everywhere, so it lives here once, over a structural node type.
 *
 * The type is STRUCTURAL on purpose: it accepts any object with an `id`. The
 * containment helpers must not care whether they are holding a Page, a
 * credential or a row read out of PocketBase, because they do not need to.
 */

/** The minimum a node needs to participate in containment. */
export interface ContainmentNode {
  id: string;
  /** The parent entity's id. `null`/`undefined` = this is a root. */
  parentId?: string | null;
  /** Position among siblings. `null`/`undefined` = unsorted, sorts last. */
  order?: number | null;
  /**
   * F 223 — the NAME of this node's relationship to its parent. Optional:
   * absent is an unnamed child, which is the state every pre-F 223 node is
   * already in. `childrenOf(nodes, id)` ignores it; `childrenOf(nodes, id,
   * 'heading')` scopes to it.
   */
  edge?: string;
  // NOTE: no index signature. An index signature makes this type accept
  // anything, but it also makes it IMPOSSIBLE to assign a plain typed entity to
  // one — `BaseEntity` was not assignable to it, which defeated the whole
  // structural point. Objects carrying extra fields still assign fine; excess
  // property checks only bite on bare literals.
}

/**
 * Sibling ordering.
 *
 * `order` first, then `id` as the tiebreak. The tiebreak is not decoration:
 * without it, two siblings with the same (or absent) `order` come back in
 * whatever order the source array happened to have, which makes a round-trip
 * through this module non-deterministic — a tree that reshuffles on every save.
 * Nodes with no `order` sort AFTER ordered ones rather than before them: an
 * unset order means "not positioned yet", which is a different state from
 * "first".
 */
export function compareSiblings(a: ContainmentNode, b: ContainmentNode): number {
  const ao = typeof a.order === 'number' ? a.order : null;
  const bo = typeof b.order === 'number' ? b.order : null;
  if (ao === null && bo === null) return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  if (ao === null) return 1;   // unset sorts last
  if (bo === null) return -1;
  if (ao !== bo) return ao - bo;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Index the nodes by id once, so a chain walk is O(1) per hop. */
export function indexNodes<T extends ContainmentNode>(
  nodes: readonly T[],
): Map<string, T> {
  const m = new Map<string, T>();
  for (const n of nodes) m.set(n.id, n);
  return m;
}

/**
 * Direct children of `parentId`, in sibling order. (F 223 — `edge` is optional.)
 *
 * `edge` is a SCOPE, not a filter that changes the meaning of the result:
 *   - omitted  -> every child of `parentId`, across all edges
 *   - provided -> only the children on THAT edge
 *
 * Omitting it is exactly the pre-F 223 behaviour, so every existing call site
 * keeps its current result. The `null` argument is the unnamed scope, which is
 * what pre-F 223 children are: `childrenOf(nodes, id, null)` returns only the
 * children that carry no edge name.
 */
export function childrenOf<T extends ContainmentNode>(
  nodes: readonly T[],
  parentId: string,
  edge?: string | null,
): T[] {
  const inParent = nodes.filter((n) => (n.parentId ?? null) === parentId);
  const scoped = edge === undefined
    ? inParent
    : inParent.filter((n) => (n.edge ?? null) === (edge ?? null));
  return scoped.sort(compareSiblings);
}

/**
 * F 223 — the children of `parentId` on one edge, EXCLUDING `id` itself.
 *
 * This is the "edit this one" read: a CTA re-rendered after a change needs the
 * other children of the same edge to lay out against, and must not be handed
 * the entity it is about to replace as though it were a sibling that still
 * exists.
 *
 * The signature is (nodes, parentId, edge?, id) so the two filters compose in
 * one pass. `id` is optional: omitting it is exactly `childrenOf`.
 */
export function scopedSiblings<T extends ContainmentNode>(
  nodes: readonly T[],
  parentId: string,
  edge: string | null | undefined,
  id?: string,
): T[] {
  const all = childrenOf(nodes, parentId, edge);
  return id === undefined ? all : all.filter((n) => n.id !== id);
}

/** Nodes with no parent. */
export function rootsOf<T extends ContainmentNode>(nodes: readonly T[]): T[] {
  return nodes.filter((n) => (n.parentId ?? null) === null).sort(compareSiblings);
}

/**
 * Walk up the parent chain, NEAREST ANCESTOR FIRST.
 *
 * Stops at a node already visited. A cycle in stored data is a corruption the
 * helpers did not cause, but they must not hang on it — so this terminates on
 * malformed input rather than trusting it.
 */
export function ancestorsOf<T extends ContainmentNode>(
  nodes: readonly T[],
  id: string,
): T[] {
  const byId = indexNodes(nodes);
  const out: T[] = [];
  const seen = new Set<string>([id]);

  let cur = byId.get(id)?.parentId ?? null;
  while (cur) {
    if (seen.has(cur)) break;          // cycle in stored data
    seen.add(cur);
    const node = byId.get(cur);
    if (!node) break;                   // dangling parentId — stop, do not invent
    out.push(node);
    cur = node.parentId ?? null;
  }
  return out;
}

/** The top of the chain containing `id`. `null` if the node is unknown. */
export function rootOf<T extends ContainmentNode>(
  nodes: readonly T[],
  id: string,
): T | null {
  const chain = ancestorsOf(nodes, id);
  return chain.length ? chain[chain.length - 1] : (indexNodes(nodes).get(id) ?? null);
}

/** Depth below the root. A root is 0. */
export function depthOf<T extends ContainmentNode>(nodes: readonly T[], id: string): number {
  return ancestorsOf(nodes, id).length;
}

// ── validation ─────────────────────────────────────────────────────────────

export interface ValidationResult {
  ok: boolean;
  /** Machine-readable. `null` when ok. */
  code:
    | 'unknown-parent'
    | 'cycle'
    | 'self-parent'
    | 'has-children'
    | null;
  message?: string;
}

export function ok(): ValidationResult { return { ok: true, code: null }; }

/**
 * Can `id` be re-parented under `newParentId`?
 *
 * Two rules, in this order, because the order is what makes the error useful:
 *   1. a node cannot be its own parent
 *   2. `newParentId` must exist and must not already be a DESCENDANT of `id`
 *
 * Rule 2 is checked by walking up from the proposed parent. If the walk reaches
 * `id`, making this move would create a loop and every `ancestorsOf` from then
 * on would be wrong.
 */
export function validateMove<T extends ContainmentNode>(
  nodes: readonly T[],
  id: string,
  newParentId: string | null,
): ValidationResult {
  if (newParentId === null) return ok();                 // moving to root
  if (newParentId === id) {
    return { ok: false, code: 'self-parent', message: 'An entity cannot be its own parent.' };
  }
  const byId = indexNodes(nodes);
  if (!byId.has(newParentId)) {
    return {
      ok: false,
      code: 'unknown-parent',
      message: `No entity with id "${newParentId}". parentId must point at a real entity.`,
    };
  }
  // walk up from the PROPOSED parent looking for `id`
  const seen = new Set<string>();
  let cur: string | null = newParentId;
  while (cur) {
    if (cur === id) {
      return {
        ok: false,
        code: 'cycle',
        message: 'That move would make the entity its own ancestor.',
      };
    }
    if (seen.has(cur)) break;    // pre-existing cycle in the data; stop cleanly
    seen.add(cur);
    cur = byId.get(cur)?.parentId ?? null;
  }
  return ok();
}

/**
 * Can `id` be deleted?
 *
 * A node with children is REFUSED unless the operator explicitly overrides.
 * The override is a named parameter, not a flag on the node, so "delete this
 * whole subtree" is always a deliberate act at the call site rather than a
 * property that got persisted on the data.
 */
export function validateDelete<T extends ContainmentNode>(
  nodes: readonly T[],
  id: string,
  opts: { operatorApproved?: boolean } = {},
): ValidationResult & { childCount: number } {
  const kids = childrenOf(nodes, id);
  if (kids.length && !opts.operatorApproved) {
    return {
      ok: false,
      code: 'has-children',
      message:
        `This entity has ${kids.length} child${kids.length === 1 ? '' : 'ren'}. ` +
        'Deleting it would orphan them. Pass operatorApproved to remove the subtree.',
      childCount: kids.length,
    };
  }
  return { ...ok(), childCount: kids.length };
}

/** The whole subtree rooted at `id`, including `id` itself, children first. */
export function subtreeOf<T extends ContainmentNode>(
  nodes: readonly T[],
  id: string,
): T[] {
  const out: T[] = [];
  const byId = indexNodes(nodes);
  const seen = new Set<string>();
  // Push the node AFTER its children, so the root lands last — that is what
  // "children first" means, and it is the order a cascade-delete wants.
  // My first version never pushed `id` at all and silently returned the
  // subtree MINUS the root.
  const walk = (node: T) => {
    if (seen.has(node.id)) return;   // pre-existing cycle
    seen.add(node.id);
    for (const c of childrenOf(nodes, node.id)) {
      const child = byId.get(c.id);
      if (child) walk(child);
    }
    out.push(node);
  };
  const start = byId.get(id);
  if (start) walk(start);
  return out;
}